import React, { useEffect, useState, useMemo } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Loader from "../../components/Loader";
import SEO from "../../components/seo";
import LayoutOne from "../../layouts/LayoutOne";
import Breadcrumb from "../../wrappers/breadcrumb/Breadcrumb";
import { Container, Row, Col, Card, Accordion } from "react-bootstrap";

import Table from "../../components/Table";

import {
  authSelector,
  getReturnDetails,
  initializeSubstituteOrder,
  updateReturnHeader,
} from "../../store/slices/auth-slice";

import { createColumnHelper } from "@tanstack/react-table";
import { formatDate, getCurrencySymbol } from "../../helpers/common";

const ReturnDetails = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {t} = useTranslation();
  const { orderId, returnId } = useParams();
  const { pathname } = useLocation();

  const { isLoading } = useSelector(authSelector);

  const [returnDetails, setReturnDetails] = useState(null);

  useEffect(() => {
    if (!returnId) return;
    fetchReturnDetails(returnId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [returnId, orderId]);

  const fetchReturnDetails = async (returnId) => {
    try {
      dispatch(
        getReturnDetails({
          returnId,
          setReturnDetails: (data) => {
            setReturnDetails(data);
          },
        })
      );
    } catch (error) {
      console.error("Error fetching return details:", error);
    }
  };

  const columnHelper = createColumnHelper();
  const returnItemsColumns = useMemo(
    () => [
      columnHelper.accessor("returnItemSeqId", {
        header: t("Item Seq"),
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor("description", {
        header: t("Product"),
        cell: ({ row, getValue }) => {
          const productName = getValue() || "";
          const productId = row?.original?.productId;
          return (
            <Link to={`/product/${productId}`}>
              {productName} ({productId})
            </Link>
          );
        },
      }),
      columnHelper.accessor("returnReasonDesc", {
        header: t("Reason"),
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor("returnTypeDesc", {
        header: t("Type"),
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor("returnItemStatus", {
        header: t("Status"),
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor("returnQuantity", {
        header: t("Return Qty"),
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor("returnPrice", {
        header: t("Return Price"),
        cell: (item) =>
          `${getCurrencySymbol(
            returnDetails?.currencyUomId
          )}${item.getValue()}`,
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columnHelper]
  );

  const substituteOrdersColumns = useMemo(
    () => [
      columnHelper.accessor("orderId", {
        header: t("Order ID"),
        cell: ({ row, getValue }) => {
          const orderId = getValue() || "";
          return <Link to={`/orders/${orderId}`}>{orderId}</Link>;
        },
      }),
      columnHelper.accessor("grandTotal", {
        header: t("Grand Total"),
        cell: (item) =>
          `${getCurrencySymbol(
            returnDetails?.currencyUomId
          )}${item.getValue()}`,
      }),
      columnHelper.accessor("statusDes", {
        header: t("Status"),
        // cell: (info) => info.getValue(),
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columnHelper]
  );

  const handleCreateSubstitute = () => {
    dispatch(
      initializeSubstituteOrder({
        data: {
          returnId,
          orderId,
          webSiteId: "B2BWebStore",
        },
        callback: () => {
          navigate("/products");
        },
      })
    );
  };

  const handleStatusChange = () => {
    if (!returnDetails) return;

    const payload = {
      fromPartyId: returnDetails.fromPartyId,
      returnId: returnDetails.returnId,
      entryDate: formatDate(returnDetails?.entryDate),
      originContactMechId: "",
      billingAccountId: "",
      returnHeaderTypeId: "CUSTOMER_RETURN",
      finAccountId: "",
      currencyUomId: returnDetails.currencyUomId,
      statusId: "SUP_RETURN_ACCEPTED",
      createdBy: returnDetails.createdBy,
      paymentMethodId: "",
      needsInventoryReceive: returnDetails.needsInventoryReceive || "",
      toPartyId: returnDetails.toPartyId || "",
      destinationFacilityId: returnDetails.destinationFacilityId || "",
      supplierRmaId: "",
    };

    dispatch(
      updateReturnHeader({
        data: payload,
        callback: () => {
          fetchReturnDetails(returnId);
        },
      })
    );
  };

  return (
    <Loader loading={isLoading}>
      <SEO titleTemplate={t("Return Details")} description={t("Return Details.")} />
      <LayoutOne headerTop="visible">
        <Breadcrumb
          pages={[
            { label: t("Home"), path: process.env.PUBLIC_URL + "/" },
            {
              label: t("Return Details"),
              path: process.env.PUBLIC_URL + pathname,
            },
          ]}
        />

        <div className="cart-main-area pt-20 pb-100">
          <Container>
            {returnDetails ? (
              <>
                <div className="d-flex justify-content-end align-items-center btn-hover mb-2">
                  {/* {returnDetails?.statusId !== 'SUP_RETURN_ACCEPTED' && (
                    <button
                      className="default-btn me-2"
                      onClick={handleStatusChange}
                    >
                      Accept
                    </button>
                  )} */}
                  <button
                    className="default-btn"
                    onClick={handleCreateSubstitute}
                  >
                    {t("Create Substitute Order")}
                  </button>
                </div>
                {/* SUMMARY CARD */}
                <Card className="mb-4">
                  <Card.Header>
                    <h5 className="mb-0">{t("Return Summary")}</h5>
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      <Col md={6}>
                        <DetailRow
                          label={t("Return ID")}
                          value={returnDetails.returnId}
                        />
                        <DetailRow
                          label={t("Return Status")}
                          value={returnDetails.returnStatus}
                        />
                        <DetailRow
                          label={t("Return Type")}
                          value={returnDetails.returnHeaderTypeDesc}
                        />
                        <DetailRow
                          label={t("Maximum Returnable Value")}
                          value={
                            returnDetails?.returnValue
                              ? getCurrencySymbol(
                                  returnDetails?.currencyUomId
                                ) + returnDetails?.returnValue
                              : t("N/A")
                          }
                        />
                        <DetailRow
                          label={t("Minimum Substitute Order Value")}
                          value={
                            returnDetails?.orderValue
                              ? getCurrencySymbol(
                                  returnDetails?.currencyUomId
                                ) + returnDetails?.orderValue
                              : t("N/A")
                          }
                        />
                        <DetailRow
                          label={t("Original Order ID")}
                          value={
                            <Link to={`/orders/${orderId}`}>{orderId}</Link>
                          }
                        />
                        <DetailRow
                          label={t("Created By")}
                          value={returnDetails.createdBy || "—"}
                        />
                      </Col>
                      <Col md={6}>
                        <DetailRow
                          label={t("From Party")}
                          value={`${returnDetails.fromPartyName} (${returnDetails.fromPartyId})`}
                        />
                        <DetailRow
                          label={t("To Party")}
                          value={`${returnDetails.toPartyName} (${returnDetails.toPartyId})`}
                        />
                        <DetailRow
                          label={t("Facility")}
                          value={`${returnDetails.destinationFacilityIdDesc} (${returnDetails.destinationFacilityId})`}
                        />
                        <DetailRow
                          label={t("Currency")}
                          value={returnDetails.currencyUomId}
                        />
                        <DetailRow
                          label={t("Needs Inventory Receive")}
                          value={
                            returnDetails.needsInventoryReceive === "N"
                              ? t("No")
                              : t("Yes")
                          }
                        />

                        <DetailRow
                          label={t("Entry Date")}
                          value={formatDate(returnDetails?.entryDate)}
                        />
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>

                {/* RETURN ITEMS ACCORDION */}
                <Accordion defaultActiveKey="1" className="mt-3">
                  <Accordion.Item eventKey="1">
                    <Accordion.Header>{t("Returned Items")}</Accordion.Header>
                    <Accordion.Body>
                      <Table
                        data={returnDetails.returnItemDetails || []}
                        columns={returnItemsColumns}
                        enableGlobalFilter={false}
                        enableSorting={false}
                        enablePagination={false}
                      />
                    </Accordion.Body>
                  </Accordion.Item>
                </Accordion>

                {/* Substitute Orders  */}
                <Accordion defaultActiveKey="1" className="mt-3">
                  <Accordion.Item eventKey="1">
                    <Accordion.Header>{t("Substitute Orders")}</Accordion.Header>
                    <Accordion.Body>
                      <Table
                        data={returnDetails?.substituteOrders || []}
                        columns={substituteOrdersColumns}
                        enableGlobalFilter={false}
                        enableSorting={false}
                        enablePagination={false}
                      />
                    </Accordion.Body>
                  </Accordion.Item>
                </Accordion>
              </>
            ) : (
              <p>{t("No return details available.")}</p>
            )}
          </Container>
        </div>
      </LayoutOne>
    </Loader>
  );
};

export default ReturnDetails;

/**
 * A small helper component for summary labels/values
 */
const DetailRow = ({ label, value }) => {
  return (
    <p className="mb-1">
      <strong style={{ fontWeight: 600 }}>{label}:</strong> {value}
    </p>
  );
};
